/**
 * FDV — loader del motor de campañas. Doc 16 §3.2 / §3.3 / §7 paso 3.
 *
 * Siempre carga (~pequeño). Evalúa audiencia / agenda / frecuencia / holdout
 * contra la config horneada en <script id="fdv-campaigns">, registra el trigger
 * compartido, y cuando uno dispara para una campaña elegible hace
 * `import('./fdv-campaign.js')` (componente + estilos = peso real, solo entonces).
 *
 * Guardarraíl (surface manager): 1 solo popup interruptivo por sesión.
 * Holdout: `holdout_pct` por campaña + holdout global (cookie `fdv_gam_holdout`).
 *
 * Publica `window.FDV.campaigns` = { markConverted(key), _config } para que el
 * componente registre la conversión.
 */

const CFG_ID = 'fdv-campaigns';
const SESSION_INTERRUPT_KEY = 'fdv_camp_interrupted';
const GLOBAL_HOLDOUT_COOKIE = 'fdv_gam_holdout';
const PV_KEY = 'fdv_camp_pv';
const INTERRUPTIVE_FORMATS = ['center_modal', 'slide_in', 'bottom_banner'];

const log = (...a) => {
  if (window.FDV?.gifts?.debug || readConfig()?.bootstrap?.debug) console.log('[fdv-campaign]', ...a);
};

/* --------------------------------------------------------------- storage -- */

const ls = {
  get(k) {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k, v) {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* noop */
    }
  },
};
const ss = {
  get(k) {
    try {
      return sessionStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k, v) {
    try {
      sessionStorage.setItem(k, v);
    } catch {
      /* noop */
    }
  },
};

function cookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

/* ----------------------------------------------------------------- config -- */

let _config;
function readConfig() {
  if (_config !== undefined) return _config;
  const el = document.getElementById(CFG_ID);
  try {
    _config = el ? JSON.parse(el.textContent || '{}') : null;
  } catch {
    _config = null;
  }
  return _config;
}

/* ------------------------------------------------------------- elegibilidad -- */

const DOW = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function isNewVisitor() {
  return !cookie('fdv_seen') && !ls.get('fdv_seen');
}
function markSeen() {
  ls.set('fdv_seen', String(Date.now()));
  try {
    document.cookie = 'fdv_seen=1; max-age=31536000; path=/; samesite=lax';
  } catch {
    /* noop */
  }
}

function deviceKind() {
  return matchMedia('(max-width: 749px)').matches || 'ontouchstart' in window ? 'mobile' : 'desktop';
}

/**
 * ¿Sabemos quién es este visitante? Login real, o id de cliente RECLAMADO desde
 * un enlace de correo (`?c_id=` → `fdv-analytics.js`). Se usa para no pedirle el
 * correo a quien evidentemente ya es cliente/suscriptor.
 */
function isKnownVisitor(boot) {
  if (boot && boot.customerLoggedIn) return true;
  try {
    if (window.FDV && window.FDV.claimedCustomerId) return true;
    if (/^\d{1,15}$/.test(ls.get('fdv_claimed_cid') || '')) return true;
  } catch {
    /* noop */
  }
  return false;
}

function audienceMatches(c, boot) {
  const a = c.audience || {};

  // Campaña de pura captura de correo (`goal: lead_capture` + `game: none`,
  // p. ej. el popup de bienvenida) → no mostrarla a alguien que ya conocemos.
  const pureCapture = (c.goal || 'lead_capture') === 'lead_capture' && (c.game || 'none') === 'none';
  if (pureCapture && isKnownVisitor(boot)) return false;

  if (a.newReturning === 'new' && !isNewVisitor()) return false;
  if (a.newReturning === 'returning' && isNewVisitor()) return false;

  if (a.cart === 'has_items' && (boot.cartItemCount || 0) === 0) return false;
  if (a.cart === 'empty' && (boot.cartItemCount || 0) > 0) return false;

  if (a.device && a.device !== 'any' && a.device !== deviceKind()) return false;

  if (Array.isArray(a.pages) && a.pages.length) {
    const path = location.pathname.toLowerCase();
    const hit = a.pages.some((p) => {
      const s = String(p || '').toLowerCase().trim();
      return s && (path === s || path.startsWith(s + '/') || path.includes('/' + s));
    });
    if (!hit) return false;
  }

  if (a.utm) {
    const qs = new URLSearchParams(location.search);
    const hay = `${qs.get('utm_source') || ''} ${qs.get('utm_campaign') || ''} ${qs.get('utm_medium') || ''}`.toLowerCase();
    if (!hay.includes(String(a.utm).toLowerCase())) return false;
  }

  return true;
}

function scheduleDaysOk(c) {
  if (!Array.isArray(c.scheduleDays) || !c.scheduleDays.length) return true;
  return c.scheduleDays.map((d) => String(d).toLowerCase()).includes(DOW[new Date().getDay()]);
}

function freqState(key) {
  try {
    return JSON.parse(ls.get('fdv_camp:' + key) || '{}');
  } catch {
    return {};
  }
}

function frequencyOk(c) {
  const st = freqState(c.key);
  if (c.suppressAfterConvert && st.converted) return false;

  switch (c.frequency) {
    case 'once_session':
      return ss.get('fdv_camp_s:' + c.key) !== '1';
    case 'once_day':
      return !st.shownAt || Date.now() - st.shownAt > 864e5;
    case 'once_week':
      return !st.shownAt || Date.now() - st.shownAt > 6048e5;
    case 'once_n_days':
      return !st.shownAt || Date.now() - st.shownAt > (Number(c.frequencyDays) || 1) * 864e5;
    default:
      return true;
  }
}

function markShown(c) {
  const st = freqState(c.key);
  st.shownAt = Date.now();
  ls.set('fdv_camp:' + c.key, JSON.stringify(st));
  ss.set('fdv_camp_s:' + c.key, '1');
}

function markConverted(key) {
  const st = freqState(key);
  st.converted = true;
  ls.set('fdv_camp:' + key, JSON.stringify(st));
}

/* ------------------------------------------------------------- holdout / A-B -- */

function hash01to100(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  return h % 100;
}

function anonId() {
  return window.FDV?.anonId || cookie('fdv_anon_id') || 'anon';
}

function isHoldout(c) {
  const pct = c.holdoutPct == null ? 10 : Number(c.holdoutPct);
  return hash01to100(anonId() + '|' + c.key + '|holdout') < pct;
}

function abVariant(c) {
  if (!c.variantB) return 'a';
  return hash01to100(anonId() + '|' + c.key + '|ab') < 50 ? 'a' : 'b';
}

/* --------------------------------------------------------- surface manager -- */

const surfaceManager = {
  canShow(c) {
    if (!INTERRUPTIVE_FORMATS.includes(c.format)) return true;
    return ss.get(SESSION_INTERRUPT_KEY) !== '1';
  },
  claim(c) {
    if (INTERRUPTIVE_FORMATS.includes(c.format)) ss.set(SESSION_INTERRUPT_KEY, '1');
  },
};

/* ------------------------------------------------------------------ loader -- */

class CampaignLoader {
  #candidates = [];
  #fired = new Set();
  #cleanups = [];

  init() {
    const config = readConfig() || { bootstrap: {}, campaigns: [] };

    // Siempre exponer la API (backendUrl / markConverted) para las superficies
    // que montan <fdv-campaign> por su cuenta (p. ej. la ruleta post-compra).
    window.FDV = window.FDV || {};
    window.FDV.campaigns = { markConverted, _config: config };

    if (!Array.isArray(config.campaigns) || !config.campaigns.length) return;
    if (config.bootstrap?.debug) log('config', config);

    // Editor de temas: no dispares campañas (ensuciaría el preview).
    if (config.bootstrap?.debug === true) {
      markSeen();
      return;
    }

    // Holdout global: nunca ve ninguna campaña.
    if (cookie(GLOBAL_HOLDOUT_COOKIE) === '1') {
      markSeen();
      return;
    }

    // Contador de pageviews (para el trigger `page_count`).
    const pv = (Number(ls.get(PV_KEY)) || 0) + 1;
    ls.set(PV_KEY, String(pv));

    const boot = config.bootstrap || {};
    this.#candidates = config.campaigns.filter(
      (c) => audienceMatches(c, boot) && scheduleDaysOk(c) && frequencyOk(c)
    );
    markSeen();

    if (!this.#candidates.length) {
      log('sin campañas elegibles');
      return;
    }
    log(
      'candidatas:',
      this.#candidates.map((c) => c.key)
    );

    this.#registerTriggers(pv);
  }

  #registerTriggers(pv) {
    const byTrigger = new Map();
    for (const c of this.#candidates) {
      if (!byTrigger.has(c.trigger)) byTrigger.set(c.trigger, []);
      byTrigger.get(c.trigger).push(c);
    }

    for (const [trigger, list] of byTrigger) {
      const fire = () => this.#tryFire(list);
      switch (trigger) {
        case 'immediate':
          this.#defer(fire, 1500);
          break;
        case 'url_param': {
          const href = location.href.toLowerCase();
          if (list.some((c) => c.triggerValue && href.includes(String(c.triggerValue).toLowerCase())))
            this.#defer(fire, 600);
          break;
        }
        case 'page_count': {
          const n = Math.max(...list.map((c) => Number(c.triggerValue) || 2));
          if (pv >= n) this.#defer(fire, 2500);
          break;
        }
        case 'time_on_page': {
          const secs = Math.min(...list.map((c) => Number(c.triggerValue) || 20));
          this.#defer(fire, secs * 1000);
          break;
        }
        case 'idle_seconds':
          this.#onIdle(Math.min(...list.map((c) => Number(c.triggerValue) || 25)), fire);
          break;
        case 'scroll_depth':
          this.#onScrollDepth(Math.min(...list.map((c) => Number(c.triggerValue) || 60)), fire);
          break;
        case 'exit_intent':
          this.#onExitIntent(fire);
          break;
        default:
          break;
      }
    }
  }

  #defer(fn, ms) {
    const t = setTimeout(fn, ms);
    this.#cleanups.push(() => clearTimeout(t));
  }

  #onIdle(secs, fire) {
    let t;
    const reset = () => {
      clearTimeout(t);
      t = setTimeout(fire, secs * 1000);
    };
    const evts = ['mousemove', 'keydown', 'scroll', 'touchstart', 'pointerdown'];
    evts.forEach((e) => addEventListener(e, reset, { passive: true }));
    this.#cleanups.push(() => {
      clearTimeout(t);
      evts.forEach((e) => removeEventListener(e, reset));
    });
    reset();
  }

  #onScrollDepth(pct, fire) {
    const onScroll = () => {
      const h = document.documentElement;
      const depth = ((h.scrollTop + innerHeight) / h.scrollHeight) * 100;
      if (depth >= pct) fire();
    };
    addEventListener('scroll', onScroll, { passive: true });
    this.#cleanups.push(() => removeEventListener('scroll', onScroll));
  }

  #onExitIntent(fire) {
    // Desktop: el cursor sale por arriba del viewport.
    const onOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) fire();
    };
    document.addEventListener('mouseout', onOut);
    this.#cleanups.push(() => document.removeEventListener('mouseout', onOut));

    // Móvil: scroll-up rápido tras haber bajado (proxy débil, aceptado — doc §3.2).
    if (deviceKind() === 'mobile') {
      let lastY = scrollY;
      let lastT = Date.now();
      let wentDown = false;
      const onScroll = () => {
        const y = scrollY;
        const now = Date.now();
        if (y > lastY) wentDown = wentDown || y > 500;
        else if (wentDown && lastY - y > 180 && now - lastT < 350) fire();
        lastY = y;
        lastT = now;
      };
      addEventListener('scroll', onScroll, { passive: true });
      this.#cleanups.push(() => removeEventListener('scroll', onScroll));
    }
  }

  async #tryFire(list) {
    // Consent: si el visitante rechazó analítica/marketing, nada de campañas.
    const consent = window.FDV?.consent;
    if (consent && consent.marketing === false && consent.analytics === false) return;

    for (const c of list) {
      if (this.#fired.has(c.key)) continue;
      if (!frequencyOk(c)) continue; // pudo cambiar (otra pestaña)
      if (!surfaceManager.canShow(c)) continue;

      this.#fired.add(c.key);
      const holdout = isHoldout(c);
      const variant = abVariant(c);

      window.FDV?.track?.('fdv_campaign_eligible', { key: c.key, holdout, variant });

      if (holdout) {
        markShown(c); // no re-evaluar esta sesión
        log('holdout', c.key);
        return;
      }

      surfaceManager.claim(c);
      markShown(c);
      this.#teardown();

      try {
        // URL con `?v=` de `asset_url` (horneada por el snippet). Sin ella, el
        // `import()` relativo resuelve a `assets/fdv-campaign.js` SIN query y la
        // CDN de Shopify lo sirve cacheado ~1 año → un deploy de este chunk no
        // se propaga. Con la URL versionada, cada cambio = URL nueva = fetch.
        const modUrl = readConfig()?.bootstrap?.campaignJsUrl || './fdv-campaign.js';
        const mod = await import(modUrl);
        mod.mountCampaign(c, variant);
      } catch (e) {
        console.warn('[fdv-campaign] no se pudo cargar el componente', e);
        window.FDV?.track?.('fdv_campaign_error', { key: c.key, stage: 'import' });
      }
      return;
    }
  }

  #teardown() {
    this.#cleanups.forEach((fn) => {
      try {
        fn();
      } catch {
        /* noop */
      }
    });
    this.#cleanups = [];
  }
}

/* -------------------------------------------------------------------- init -- */

const loader = new CampaignLoader();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => loader.init(), { once: true });
} else {
  loader.init();
}
